import React, { useState } from 'react';
import { apiService } from '../../../services/api';
import PhoneInput from '../../ui/PhoneInput';
import VehicleInterestCard from '../../shared/VehicleInterestCard';
import VehicleSelector, { PublicVehicle } from '../../shared/VehicleSelector';

/* ─── Types ─── */
type Lang = 'en' | 'es';

interface CreditWizardProps {
  language?: Lang;
  onClose: () => void;
  copy?: any; // legacy compat — ignored if language is set
  vehicles?: Array<{
    id: string;
    year?: number;
    make?: string;
    model?: string;
    asking_price?: number;
  }>;
}

/* ─── Translations ─── */
const T: Record<Lang, Record<string, string>> = {
  en: {
    title: 'Credit Application',
    step0: 'Vehicle Selection',
    step1: 'Personal Information',
    step2: 'Residential Information',
    step3: 'Employment & Financial',
    step4: 'Review & Submit',
    vehicleStepTitle: 'Which vehicle(s) are you interested in?',
    vehicleStepDesc: "Select one or more vehicles you'd like to learn about financing for.",
    notSureYet: "I'm not sure yet — I'd like to browse",
    describeVehicle: "Describe what kind of vehicle you're looking for...",
    required: 'Required',
    next: 'Next',
    back: 'Back',
    submit: 'Submit Application',
    submitting: 'Submitting...',
    successTitle: 'Application Submitted!',
    successMsg: 'Your reference number is',
    successNote: 'We will contact you within 24 hours to review your application.',
    errorMsg: 'Something went wrong. Please try again.',
    close: 'Close',
    // Step 1
    firstName: 'First Name',
    middleInitial: 'M.I.',
    lastName: 'Last Name',
    email: 'Email',
    cellPhone: 'Cell Phone',
    homePhone: 'Home Phone (Optional)',
    dob: 'Date of Birth',
    ssn4: 'SSN Last 4 Digits',
    dlNumber: "Driver's License #",
    dlState: 'DL State',
    // Step 2
    streetAddress: 'Street Address',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    housingType: 'Housing Type',
    rent: 'Rent',
    own: 'Own',
    other: 'Other',
    monthlyRent: 'Monthly Rent/Mortgage',
    yearsAtAddress: 'Years at Address',
    // Step 3
    employmentStatus: 'Employment Status',
    employed: 'Employed',
    selfEmployed: 'Self-Employed',
    retired: 'Retired',
    unemployed: 'Unemployed',
    employerName: 'Employer Name',
    jobTitle: 'Title / Position',
    employerPhone: 'Employer Phone',
    monthlyIncome: 'Monthly Gross Income',
    yearsAtJob: 'Years at Job',
    incomeType: 'Income Type',
    incomeTypes:
      'Comp Paystub,Printed Paystub,Self-Emp Bank Stmt,Job Letter,SSI,Disability,Child Support,Student,Passive Income,Ride Share,Other',
    downPayment: 'Down Payment',
    desiredPayment: 'Desired Monthly Payment',
    // Step 4
    vehicleInterest: 'Vehicle of Interest',
    selectVehicle: 'Select a vehicle (optional)',
    vehicleText: 'Or describe the vehicle',
    notes: 'Additional Notes',
    notesPlaceholder: 'Anything else we should know?',
    coBuyer: 'Add a Co-Buyer',
    coBuyerFirst: 'Co-Buyer First Name',
    coBuyerLast: 'Co-Buyer Last Name',
    coBuyerPhone: 'Co-Buyer Phone',
    coBuyerEmail: 'Co-Buyer Email',
    coBuyerRelationship: 'Relationship',
    spouse: 'Spouse',
    parent: 'Parent',
    relative: 'Relative',
    consentMarketing: 'I consent to receive marketing text messages.',
    consentPrivacy: 'I accept the Privacy Policy and authorize credit verification.',
    legalNotice:
      'By submitting, I certify the information is correct and authorize the dealership and its finance partners to obtain credit reports and verify employment.',
    reviewTitle: 'Application Summary',
  },
  es: {
    title: 'Solicitud de Crédito',
    step0: 'Selección de Vehículo',
    step1: 'Información Personal',
    step2: 'Información Residencial',
    step3: 'Empleo y Finanzas',
    step4: 'Revisar y Enviar',
    vehicleStepTitle: '¿En qué vehículo(s) está interesado?',
    vehicleStepDesc:
      'Seleccione uno o más vehículos sobre los que le gustaría obtener financiamiento.',
    notSureYet: 'Aún no estoy seguro — me gustaría explorar',
    describeVehicle: 'Describa qué tipo de vehículo está buscando...',
    required: 'Requerido',
    next: 'Siguiente',
    back: 'Atrás',
    submit: 'Enviar Solicitud',
    submitting: 'Enviando...',
    successTitle: '¡Solicitud Enviada!',
    successMsg: 'Su número de referencia es',
    successNote: 'Nos comunicaremos con usted dentro de 24 horas para revisar su solicitud.',
    errorMsg: 'Algo salió mal. Intente de nuevo.',
    close: 'Cerrar',
    firstName: 'Nombre',
    middleInitial: 'Inicial',
    lastName: 'Apellido',
    email: 'Correo Electrónico',
    cellPhone: 'Teléfono Celular',
    homePhone: 'Teléfono de Casa (Opcional)',
    dob: 'Fecha de Nacimiento',
    ssn4: 'Últimos 4 del SSN',
    dlNumber: '# Licencia de Conducir',
    dlState: 'Estado de Licencia',
    streetAddress: 'Dirección',
    city: 'Ciudad',
    state: 'Estado',
    zipCode: 'Código Postal',
    housingType: 'Tipo de Vivienda',
    rent: 'Alquiler',
    own: 'Propia',
    other: 'Otro',
    monthlyRent: 'Renta/Hipoteca Mensual',
    yearsAtAddress: 'Años en la Dirección',
    employmentStatus: 'Estado Laboral',
    employed: 'Empleado',
    selfEmployed: 'Independiente',
    retired: 'Jubilado',
    unemployed: 'Desempleado',
    employerName: 'Nombre del Empleador',
    jobTitle: 'Cargo / Posición',
    employerPhone: 'Teléfono del Empleador',
    monthlyIncome: 'Ingreso Mensual Bruto',
    yearsAtJob: 'Años en el Trabajo',
    incomeType: 'Tipo de Ingreso',
    incomeTypes:
      'Nómina Comp,Nómina Impresa,Estado Bancario,Carta Laboral,SSI,Discapacidad,Pensión Alimenticia,Estudiante,Ingreso Pasivo,Ride Share,Otro',
    downPayment: 'Pago Inicial',
    desiredPayment: 'Pago Mensual Deseado',
    vehicleInterest: 'Vehículo de Interés',
    selectVehicle: 'Seleccione un vehículo (opcional)',
    vehicleText: 'O describa el vehículo',
    notes: 'Notas Adicionales',
    notesPlaceholder: '¿Algo más que debamos saber?',
    coBuyer: 'Agregar Co-Comprador',
    coBuyerFirst: 'Nombre del Co-Comprador',
    coBuyerLast: 'Apellido del Co-Comprador',
    coBuyerPhone: 'Teléfono del Co-Comprador',
    coBuyerEmail: 'Email del Co-Comprador',
    coBuyerRelationship: 'Relación',
    spouse: 'Cónyuge',
    parent: 'Padre/Madre',
    relative: 'Familiar',
    consentMarketing: 'Acepto recibir mensajes de texto promocionales.',
    consentPrivacy: 'Acepto la Política de Privacidad y autorizo verificación de crédito.',
    legalNotice:
      'Al enviar, certifico que la información es correcta y autorizo al concesionario y sus socios financieros a obtener reportes de crédito y verificar empleo.',
    reviewTitle: 'Resumen de Solicitud',
  },
};

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

/* ─── Styles ─── */
const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors';
const inputErrCls =
  'w-full rounded-lg border-2 border-red-400 px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
const sectionCls =
  'text-sm font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-200 pb-2 mb-4';

/* ─── Component ─── */
const CreditAppWizard: React.FC<CreditWizardProps> = ({
  language = 'en',
  onClose,
  vehicles: _vehicles,
}) => {
  const t = T[language] || T.en;
  const [step, setStep] = useState(0);
  const totalSteps = 5;

  // Form data
  const [form, setForm] = useState<Record<string, string>>({});
  const [selectedVehicles, setSelectedVehicles] = useState<PublicVehicle[]>([]);
  const [notSureYet, setNotSureYet] = useState(false);
  const [showCoBuyer, setShowCoBuyer] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refNumber, setRefNumber] = useState('');
  const [result, setResult] = useState<'success' | 'error' | null>(null);

  const f = (key: string) => form[key] || '';
  const set = (key: string, val: string) => setForm((p) => ({ ...p, [key]: val }));
  const touch = (key: string) => setTouched((p) => new Set(p).add(key));
  const err = (key: string) => touched.has(key) && !f(key).trim();

  const Field: React.FC<{
    name: string;
    label: string;
    required?: boolean;
    type?: string;
    maxLen?: number;
    placeholder?: string;
    half?: boolean;
  }> = ({ name, label, required, type = 'text', maxLen, placeholder, half }) => (
    <div className={half ? '' : 'col-span-2 sm:col-span-1'}>
      <label className={labelCls}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'tel' ? (
        <PhoneInput
          className={required && err(name) ? inputErrCls : inputCls}
          value={f(name)}
          onChange={(v) => set(name, v)}
          onBlur={() => required && touch(name)}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          className={required && err(name) ? inputErrCls : inputCls}
          value={f(name)}
          onChange={(e) => {
            let val = e.target.value;
            if (maxLen) val = val.slice(0, maxLen);
            if (name === 'ssn4') val = val.replace(/\D/g, '').slice(0, 4);
            set(name, val);
          }}
          onBlur={() => required && touch(name)}
          placeholder={placeholder}
          inputMode={type === 'number' ? 'decimal' : name === 'ssn4' ? 'numeric' : undefined}
        />
      )}
      {required && err(name) && <p className="mt-1 text-xs text-red-500">{t.required}</p>}
    </div>
  );

  const Select: React.FC<{
    name: string;
    label: string;
    options: Array<{ value: string; label: string }>;
    required?: boolean;
  }> = ({ name, label, options, required }) => (
    <div>
      <label className={labelCls}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        className={required && err(name) ? inputErrCls : inputCls}
        value={f(name)}
        onChange={(e) => set(name, e.target.value)}
        onBlur={() => required && touch(name)}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {required && err(name) && <p className="mt-1 text-xs text-red-500">{t.required}</p>}
    </div>
  );

  /* ─── Validation ─── */
  const validateStep = (s: number): boolean => {
    if (s === 0) return true; // Vehicle selection is optional (user can pick "Not sure yet")
    if (s === 1) {
      const required = ['firstName', 'lastName', 'cellPhone', 'email'];
      const next = new Set(touched);
      required.forEach((k) => next.add(k));
      setTouched(next);
      if (!required.every((k) => f(k).trim())) return false;
      if (!/\S+@\S+\.\S+/.test(f('email'))) {
        touch('email');
        return false;
      }
      if (f('cellPhone').replace(/\D/g, '').length < 10) {
        touch('cellPhone');
        return false;
      }
      return true;
    }
    return true; // Steps 2-4 have no hard required fields (address/employment optional for BHPH leads)
  };

  const goNext = () => {
    if (validateStep(step)) setStep(step + 1);
  };

  /* ─── Submit ─── */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setResult(null);
    try {
      const payload: Record<string, unknown> = {
        first_name: f('firstName').trim(),
        last_name: f('lastName').trim(),
        middle_initial: f('middleInitial').trim() || null,
        phone: f('cellPhone').trim(),
        home_phone: f('homePhone').trim() || null,
        email: f('email').trim(),
        date_of_birth: f('dob') || null,
        ssn_last4: f('ssn4') || null,
        dl_number: f('dlNumber').trim() || null,
        dl_state: f('dlState') || null,
        street_address: f('streetAddress').trim() || null,
        city: f('city').trim() || null,
        state: f('state') || null,
        zip_code: f('zipCode').trim() || null,
        housing_type: f('housingType') || null,
        monthly_rent_mortgage: f('monthlyRent') ? parseFloat(f('monthlyRent')) : null,
        years_at_address: f('yearsAddr') ? parseInt(f('yearsAddr')) : null,
        months_at_address: f('monthsAddr') ? parseInt(f('monthsAddr')) : null,
        employment_status: f('empStatus') || null,
        employer_name: f('empName').trim() || null,
        job_title: f('jobTitle').trim() || null,
        employer_phone: f('empPhone').trim() || null,
        monthly_income: f('income') ? parseFloat(f('income')) : null,
        years_at_job: f('yearsJob') ? parseInt(f('yearsJob')) : null,
        months_at_job: f('monthsJob') ? parseInt(f('monthsJob')) : null,
        income_type: f('incomeType') || null,
        down_payment: f('downPayment') ? parseFloat(f('downPayment')) : null,
        desired_monthly_payment: f('desiredPayment') ? parseFloat(f('desiredPayment')) : null,
        vehicle_interest_ids: selectedVehicles.map((v) => v.id),
        vehicle_interest_text: notSureYet ? f('vehicle_interest_text') || null : null,
        co_buyer_first_name: showCoBuyer ? f('coBuyerFirst').trim() || null : null,
        co_buyer_last_name: showCoBuyer ? f('coBuyerLast').trim() || null : null,
        co_buyer_phone: showCoBuyer ? f('coBuyerPhone').trim() || null : null,
        co_buyer_email: showCoBuyer ? f('coBuyerEmail').trim() || null : null,
        co_buyer_relationship: showCoBuyer ? f('coBuyerRel') || null : null,
        notes: f('notes').trim() || null,
        marketing_consent: marketingConsent,
        privacy_consent: privacyConsent,
      };
      const res = (await apiService.post('/credit-applications/submit', payload)) as any;
      setRefNumber(res?.reference_number || res?.application_id || '');
      setResult('success');
    } catch {
      setResult('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ─── Success Screen ─── */
  if (result === 'success') {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8 max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.successTitle}</h3>
          {refNumber && (
            <p className="text-lg text-gray-700 mb-1">
              {t.successMsg}:{' '}
              <span className="font-mono font-bold text-blue-600 text-xl">{refNumber}</span>
            </p>
          )}
          <p className="text-gray-500 mb-6">{t.successNote}</p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
          >
            {t.close}
          </button>
        </div>
      </div>
    );
  }

  /* ─── Progress Bar ─── */
  const ProgressBar = () => (
    <div className="flex items-center gap-1 mb-6">
      {Array.from({ length: totalSteps }, (_, i) => i).map((s) => (
        <React.Fragment key={s}>
          <div
            className={`h-2 flex-1 rounded-full transition-colors ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`}
          />
        </React.Fragment>
      ))}
    </div>
  );

  const stepLabels = [t.step0, t.step1, t.step2, t.step3, t.step4];

  const incomeTypeOptions = t.incomeTypes.split(',').map((label) => ({
    value: label.trim().toLowerCase().replace(/\s+/g, '_'),
    label: label.trim(),
  }));

  const yearOptions = Array.from({ length: 51 }, (_, i) => ({
    value: String(i),
    label: String(i),
  }));
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i),
    label: String(i),
  }));

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm max-w-2xl mx-auto overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{t.title}</h2>
          <span className="text-sm text-blue-200">
            {language === 'es' ? 'Paso' : 'Step'} {step + 1} / {totalSteps}
          </span>
        </div>
        <p className="text-sm text-blue-100 mt-0.5">{stepLabels[step]}</p>
      </div>

      <div className="px-6 pt-4">
        <ProgressBar />
      </div>

      {/* Error banner */}
      {result === 'error' && (
        <div className="mx-6 mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {t.errorMsg}
        </div>
      )}

      <div className="px-6 pb-6">
        {/* ═══ STEP 0: Vehicle Selection ═══ */}
        {step === 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">{t.vehicleStepTitle}</h3>
            <p className="text-sm text-gray-600">{t.vehicleStepDesc}</p>

            {!notSureYet && (
              <VehicleSelector
                selectedVehicles={selectedVehicles}
                onSelectionChange={setSelectedVehicles}
              />
            )}

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notSureYet}
                onChange={(e) => {
                  setNotSureYet(e.target.checked);
                  if (e.target.checked) setSelectedVehicles([]);
                }}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">{t.notSureYet}</span>
            </label>

            {notSureYet && (
              <textarea
                value={f('vehicle_interest_text') || ''}
                onChange={(e) => set('vehicle_interest_text', e.target.value)}
                placeholder={t.describeVehicle}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500"
              />
            )}
          </div>
        )}

        {/* ═══ STEP 1: Personal Information ═══ */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              <div className="col-span-2 sm:col-span-2">
                <Field name="firstName" label={t.firstName} required />
              </div>
              <div className="col-span-1 sm:col-span-1">
                <Field name="middleInitial" label={t.middleInitial} maxLen={3} half />
              </div>
              <div className="col-span-2 sm:col-span-3">
                <Field name="lastName" label={t.lastName} required />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field name="email" label={t.email} type="email" required />
              <Field name="cellPhone" label={t.cellPhone} type="tel" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field name="homePhone" label={t.homePhone} type="tel" />
              <Field name="dob" label={t.dob} type="date" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field name="ssn4" label={t.ssn4} placeholder="••••" />
              <Field name="dlNumber" label={t.dlNumber} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                name="dlState"
                label={t.dlState}
                options={US_STATES.map((s) => ({ value: s, label: s }))}
              />
            </div>
          </div>
        )}

        {/* ═══ STEP 2: Residential Information ═══ */}
        {step === 2 && (
          <div className="space-y-4">
            <Field name="streetAddress" label={t.streetAddress} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-2">
                <Field name="city" label={t.city} />
              </div>
              <Select
                name="state"
                label={t.state}
                options={US_STATES.map((s) => ({ value: s, label: s }))}
              />
              <Field name="zipCode" label={t.zipCode} maxLen={10} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select
                name="housingType"
                label={t.housingType}
                options={[
                  { value: 'rent', label: t.rent },
                  { value: 'own', label: t.own },
                  { value: 'other', label: t.other },
                ]}
              />
              <Field name="monthlyRent" label={t.monthlyRent} type="number" placeholder="$" />
              <div className="grid grid-cols-2 gap-2">
                <Select name="yearsAddr" label={t.yearsAtAddress} options={yearOptions} />
                <Select name="monthsAddr" label="Mo." options={monthOptions} />
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Employment & Financial ═══ */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className={sectionCls}>{t.employmentStatus}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                name="empStatus"
                label={t.employmentStatus}
                options={[
                  { value: 'employed', label: t.employed },
                  { value: 'self_employed', label: t.selfEmployed },
                  { value: 'retired', label: t.retired },
                  { value: 'unemployed', label: t.unemployed },
                ]}
              />
              <Select name="incomeType" label={t.incomeType} options={incomeTypeOptions} />
            </div>
            {(f('empStatus') === 'employed' || f('empStatus') === 'self_employed') && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field name="empName" label={t.employerName} />
                  <Field name="jobTitle" label={t.jobTitle} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field name="empPhone" label={t.employerPhone} type="tel" />
                  <div className="grid grid-cols-2 gap-2">
                    <Select name="yearsJob" label={t.yearsAtJob} options={yearOptions} />
                    <Select name="monthsJob" label="Mo." options={monthOptions} />
                  </div>
                </div>
              </>
            )}

            <h3 className={sectionCls + ' mt-6'}>{t.downPayment}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field name="income" label={t.monthlyIncome} type="number" placeholder="$" />
              <Field name="downPayment" label={t.downPayment} type="number" placeholder="$" />
              <Field name="desiredPayment" label={t.desiredPayment} type="number" placeholder="$" />
            </div>
          </div>
        )}

        {/* ═══ STEP 4: Review, Co-Buyer & Submit ═══ */}
        {step === 4 && (
          <div className="space-y-4">
            {/* Vehicle Summary (read-only, from Step 0) */}
            {selectedVehicles.length > 0 && (
              <div>
                <h3 className={sectionCls}>{t.vehicleInterest}</h3>
                <div className="space-y-1.5">
                  {selectedVehicles.map((v) => (
                    <VehicleInterestCard key={v.id} vehicle={v} compact />
                  ))}
                </div>
              </div>
            )}
            {notSureYet && f('vehicle_interest_text') && (
              <div>
                <h3 className={sectionCls}>{t.vehicleInterest}</h3>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {f('vehicle_interest_text')}
                </p>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className={labelCls}>{t.notes}</label>
              <textarea
                className={inputCls + ' min-h-[80px]'}
                rows={3}
                value={f('notes')}
                onChange={(e) => set('notes', e.target.value)}
                placeholder={t.notesPlaceholder}
              />
            </div>

            {/* Co-Buyer */}
            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCoBuyer}
                  onChange={(e) => setShowCoBuyer(e.target.checked)}
                  className="h-4 w-4 rounded text-blue-600"
                />
                <span className="text-sm font-medium text-gray-700">{t.coBuyer}</span>
              </label>
              {showCoBuyer && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field name="coBuyerFirst" label={t.coBuyerFirst} />
                    <Field name="coBuyerLast" label={t.coBuyerLast} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field name="coBuyerPhone" label={t.coBuyerPhone} type="tel" />
                    <Field name="coBuyerEmail" label={t.coBuyerEmail} type="email" />
                  </div>
                  <Select
                    name="coBuyerRel"
                    label={t.coBuyerRelationship}
                    options={[
                      { value: 'spouse', label: t.spouse },
                      { value: 'parent', label: t.parent },
                      { value: 'relative', label: t.relative },
                      { value: 'other', label: t.other },
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Consent */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="h-4 w-4 rounded text-blue-600 mt-0.5"
                />
                <span className="text-sm text-gray-600">{t.consentMarketing}</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyConsent}
                  onChange={(e) => setPrivacyConsent(e.target.checked)}
                  className="h-4 w-4 rounded text-blue-600 mt-0.5"
                />
                <span className="text-sm text-gray-600">{t.consentPrivacy}</span>
              </label>
              <p className="text-xs text-gray-400 leading-relaxed mt-2">{t.legalNotice}</p>
            </div>
          </div>
        )}

        {/* ═══ Navigation ═══ */}
        <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t.back}
            </button>
          ) : (
            <div />
          )}
          {step < totalSteps - 1 ? (
            <button
              onClick={goNext}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              {t.next}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {isSubmitting ? t.submitting : t.submit}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditAppWizard;
